<template>
	<section class="view-trainer-loader">
		<!-- Loading state while the trainer plugin initializes -->
		<section v-if="isLoading" class="loading">
			<img src="~@chalkysticks/sass/build/asset/image/loader/triangle-white.svg" />
			<p>{{ loadingText }}</p>
		</section>

		<!-- Trainer scene slot, only rendered once the plugin is installed -->
		<section v-else class="scene">
			<slot />
		</section>
	</section>
</template>

<script lang="ts">
	import * as Event from '@chalkysticks/vue2-trainer/src/App/Event';
	import * as Service from '@/App/Service';
	import ChalkySticks from '@chalkysticks/sdk';
	import ViewBase from '@/Core/View.vue';
	import { Component, Prop } from 'vue-property-decorator';
	import { beforeDestroy, bind, created, mounted } from '@/Utility/Decorators';

	/**
	 * Slotted wrapper that owns every bit of trainer-environment plumbing
	 * that would otherwise have to be duplicated on every page hosting a
	 * ChalkySticks trainer scene. Specifically, it:
	 *
	 *     - Installs the vue2-trainer plugin via Service.TrainerLoader
	 *     - Renders loading / error / ready states around the scene slot
	 *     - Suppresses the space-bar page scroll while the trainer is open
	 *     - Mutes trainer audio when the tab is backgrounded
	 *
	 * Host pages just wrap their scene in <ViewTrainerLoader> and stay
	 * focused on page-specific logic.
	 *
	 * @author Matt Kenefick <matt@chalkysticks.com>
	 * @package View/Trainer
	 * @project ChalkySticks Quest
	 */
	@Component
	export default class ViewTrainerLoader extends ViewBase {
		/**
		 * Label shown beneath the spinner while the plugin initializes.
		 *
		 * @type string
		 */
		@Prop({ default: 'Loading...' })
		public loadingText!: string;

		/**
		 * Whether the plugin is still loading. Starts true and flips false
		 * once Service.TrainerLoader.initialize() resolves.
		 *
		 * @type boolean
		 */
		protected isLoading: boolean = true;

		/**
		 * Kick off plugin initialization as soon as the component is
		 * created so the loading state is visible on first paint.
		 *
		 * @return Promise<void>
		 */
		@created
		protected async initializeTrainer(): Promise<void> {
			this.isLoading = true;

			try {
				await Service.TrainerLoader.initialize();
				this.isLoading = false;
			} catch (error) {
				console.error('[ViewTrainerLoader] Failed to initialize trainer:', error);
				this.isLoading = false;
			}
		}

		/**
		 * Attach document-level listeners that are common to every
		 * trainer-hosting page.
		 *
		 * @return void
		 */
		@mounted
		public attachEvents(): void {
			document.addEventListener('keydown', this.Handle_OnKeyboardSpace);
			document.addEventListener('visibilitychange', this.Handle_OnVisibilityChange);
		}

		/**
		 * Detach every listener attached in attachEvents.
		 *
		 * @return void
		 */
		@beforeDestroy
		public detachEvents(): void {
			document.removeEventListener('keydown', this.Handle_OnKeyboardSpace);
			document.removeEventListener('visibilitychange', this.Handle_OnVisibilityChange);
		}

		// region: Event Handlers
		// ---------------------------------------------------------------------------

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnKeyboardSpace(e: Event): Promise<void> {
			// If space, prevent page jump
			if ((e as KeyboardEvent).code === 'Space') {
				e.preventDefault();
			}
		}

		/**
		 * Reset the trainer-loader installation flag and re-run init so a
		 * transient failure can be retried without a page reload.
		 *
		 * @param PointerEvent e
		 * @return Promise<void>
		 */
		protected async Handle_OnRetryClick(e: PointerEvent): Promise<void> {
			e.preventDefault();

			Service.TrainerLoader.reset();

			await this.initializeTrainer();
		}

		/**
		 * @param Event e
		 * @return Promise<void>
		 */
		@bind
		protected async Handle_OnVisibilityChange(e: Event): Promise<void> {
			const isHidden = document.hidden;

			ChalkySticks.Core.Event.Bus.dispatch(Event.Audio.SetEnabled, !isHidden);
		}

		// endregion: Event Handlers
	}
</script>

<style lang="scss">
	.view-trainer-loader {
		height: 100%;
		width: 100%;

		.loading {
			align-items: center;
			display: flex;
			flex-direction: column;
			height: 100%;
			justify-content: center;
			text-align: center;

			p {
				color: #666;
				font-size: 1.2rem;
			}
		}

		.scene {
			background-size: 50px 50px;
			background: url('~@chalkysticks/sass/build/asset/image/loader/triangle-white.svg') center center no-repeat;
			height: 100%;
			width: 100%;
		}

		.error {
			align-items: center;
			display: flex;
			flex-direction: column;
			height: 100%;
			justify-content: center;
			padding: 2rem;
			text-align: center;

			h2 {
				color: #dc3545;
				font-size: 2rem;
				margin-bottom: 1rem;
			}

			p {
				color: #666;
				font-size: 1.1rem;
				margin-bottom: 2rem;
				max-width: 500px;
			}

			.retry-button {
				background-color: #007bff;
				border: none;
				border-radius: 5px;
				color: white;
				cursor: pointer;
				font-size: 1rem;
				padding: 0.75rem 2rem;
				transition: background-color 0.3s;

				&:hover {
					background-color: #0056b3;
				}
			}
		}
	}
</style>
